import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Building2, Check, X, AlertTriangle, ChevronLeft, ChevronRight, Image, FileText,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, Shield, LogOut, Menu,
  ArrowLeftRight, ZoomIn, ZoomOut, Download, Calendar
} from "lucide-react";

const CATEGORIES = [
  "Income", "Direct Expenses", "Indirect Expenses", "Internal Transfer", "VAT Payments",
  "Loan Repayments", "Owner Drawings", "Wages", "Marketing", "Software Subscriptions",
  "Rent", "Utilities", "Insurance", "Professional Fees", "Travel", "Other"
];

const EXPENSE_TYPES = [
  { value: "direct", label: "Direct Expense" },
  { value: "indirect", label: "Indirect Expense" },
  { value: "transfer", label: "Internal Transfer" },
  { value: "other", label: "Other" }
];

export default function ReviewQueuePage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [statementPreview, setStatementPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [imageZoom, setImageZoom] = useState(100);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchReviewQueue();
  }, []);

  useEffect(() => {
    if (transactions[currentIndex]) {
      setEditData({
        category: transactions[currentIndex].category,
        expense_type: transactions[currentIndex].expense_type || "other",
        is_internal_transfer: transactions[currentIndex].is_internal_transfer,
        notes: transactions[currentIndex].notes || ""
      });
      // Fetch statement preview
      fetchStatementPreview(transactions[currentIndex].transaction_id);
    }
  }, [currentIndex, transactions]);

  const fetchReviewQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/transactions/review/queue?limit=100`, { headers, withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load review queue");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatementPreview = async (transactionId) => {
    setPreviewLoading(true);
    setStatementPreview(null);
    try {
      const response = await axios.get(`${API}/transactions/${transactionId}/statement-preview`, { headers, withCredentials: true });
      setStatementPreview(response.data);
    } catch (error) {
      console.log("No statement preview available");
      setStatementPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleApprove = async () => {
    const tx = transactions[currentIndex];
    if (!tx) return;

    setSaving(true);
    try {
      // Update with edited data first
      if (editData) {
        await axios.put(`${API}/transactions/${tx.transaction_id}`, editData, { headers, withCredentials: true });
      }
      // Then approve
      await axios.post(`${API}/transactions/${tx.transaction_id}/approve`, {}, { headers, withCredentials: true });
      
      toast.success("Transaction approved");
      
      // Remove from list and move to next
      setTransactions(prev => prev.filter((_, i) => i !== currentIndex));
      if (currentIndex >= transactions.length - 1) {
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
    } catch (error) {
      toast.error("Failed to approve transaction");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    const tx = transactions[currentIndex];
    if (!tx || !editData) return;

    setSaving(true);
    try {
      await axios.put(`${API}/transactions/${tx.transaction_id}`, editData, { headers, withCredentials: true });
      toast.success("Changes saved");
      
      if (currentIndex < transactions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const currentTx = transactions[currentIndex];

  const NavLink = ({ to, icon: Icon, children, active, badge }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
      {badge > 0 && <span className="ml-auto bg-[#FCD34D] text-[#1C1917] text-xs font-bold px-2 py-0.5 rounded-full">{badge}</span>}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/transfers" icon={ArrowLeftRight}>Transfer Detection</NavLink>
          <NavLink to="/reconciliation" icon={Calendar}>Weekly Summary</NavLink>
          <NavLink to="/review" icon={ClipboardCheck} active badge={transactions.length}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Review Queue</h1>
              <p className="text-zinc-400 text-sm mt-1">
                {transactions.length > 0 
                  ? `${transactions.length} transactions need your review`
                  : 'All transactions reviewed'}
              </p>
            </div>
            {transactions.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-400">
                  {currentIndex + 1} of {transactions.length}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  onClick={() => setCurrentIndex(i => Math.min(transactions.length - 1, i + 1))}
                  disabled={currentIndex >= transactions.length - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <Card className="bg-[#121214] border-zinc-800">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-500" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">All Caught Up!</h2>
                <p className="text-zinc-400 mb-6">No transactions need review at this time.</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/upload">
                    <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                      Upload More Statements
                    </Button>
                  </Link>
                  <Link to="/transfers">
                    <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                      <ArrowLeftRight className="w-4 h-4 mr-2" />
                      Check Transfers
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : currentTx && editData && (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Panel - Statement Preview */}
              <Card className="bg-[#121214] border-zinc-800 lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-zinc-500" />
                    Source Statement
                  </CardTitle>
                  <CardDescription className="text-zinc-500">
                    Original document for verification
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {previewLoading ? (
                    <div className="flex items-center justify-center h-48">
                      <div className="w-6 h-6 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : statementPreview ? (
                    <div className="space-y-4">
                      {/* File Info */}
                      <div className="p-3 bg-zinc-900/50 rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="w-5 h-5 text-zinc-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{statementPreview.filename}</p>
                            <p className="text-xs text-zinc-500">
                              {statementPreview.file_type?.toUpperCase()} · {statementPreview.extraction_method}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500">
                          Account: {statementPreview.account_name}
                        </p>
                      </div>

                      {/* Image Preview */}
                      {statementPreview.can_preview && statementPreview.preview_base64 && (
                        <div className="space-y-2">
                          {/* Zoom Controls */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-500">Preview</span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-zinc-400 hover:text-white"
                                onClick={() => setImageZoom(z => Math.max(50, z - 25))}
                              >
                                <ZoomOut className="w-4 h-4" />
                              </Button>
                              <span className="text-xs text-zinc-500 w-10 text-center">{imageZoom}%</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-zinc-400 hover:text-white"
                                onClick={() => setImageZoom(z => Math.min(200, z + 25))}
                              >
                                <ZoomIn className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Image Container */}
                          <div className="border border-zinc-800 rounded-lg overflow-auto max-h-[400px] bg-zinc-950">
                            <img
                              src={statementPreview.preview_base64}
                              alt="Statement preview"
                              className="transition-transform duration-200"
                              style={{ 
                                width: `${imageZoom}%`,
                                minWidth: '100%'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* PDF Notice */}
                      {statementPreview.can_preview && statementPreview.preview_type === 'pdf' && (
                        <div className="p-4 bg-zinc-900/50 rounded-lg text-center">
                          <FileText className="w-10 h-10 text-red-500 mx-auto mb-2" />
                          <p className="text-sm text-zinc-400 mb-3">PDF Statement</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            onClick={() => window.open(`${API}/statements/${statementPreview.statement_id}/file`, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                        </div>
                      )}

                      {/* No Preview Available */}
                      {!statementPreview.can_preview && (
                        <div className="p-4 bg-zinc-900/50 rounded-lg text-center">
                          <FileText className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                          <p className="text-sm text-zinc-500">
                            Preview not available for this file type
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-zinc-900/50 rounded-lg text-center">
                      <FileText className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">No source statement linked</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Middle Panel - Transaction Details */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white">Extracted Data</CardTitle>
                    <Badge className="badge-warning">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {Math.round(currentTx.confidence_score * 100)}% confidence
                    </Badge>
                  </div>
                  <CardDescription className="text-zinc-500">
                    Review extracted transaction details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-zinc-900/50 rounded-lg space-y-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-500 text-sm">Date</span>
                      <span className="text-white font-mono">{formatDate(currentTx.date)}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500 text-sm block mb-1">Description</span>
                      <p className="text-white text-sm bg-zinc-800/50 p-2 rounded">{currentTx.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded">
                        <span className="text-zinc-500 text-xs block mb-1">Amount In</span>
                        <span className="text-emerald-500 font-mono text-lg">{formatCurrency(currentTx.amount_in)}</span>
                      </div>
                      <div className="p-3 bg-red-500/5 border border-red-500/20 rounded">
                        <span className="text-zinc-500 text-xs block mb-1">Amount Out</span>
                        <span className="text-red-500 font-mono text-lg">{formatCurrency(currentTx.amount_out)}</span>
                      </div>
                    </div>
                    {currentTx.balance !== null && currentTx.balance !== undefined && (
                      <div className="flex justify-between pt-2 border-t border-zinc-800">
                        <span className="text-zinc-500 text-sm">Running Balance</span>
                        <span className="text-white font-mono">{formatCurrency(currentTx.balance)}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-500 font-medium">Low Confidence Extraction</p>
                        <p className="text-xs text-zinc-400 mt-1">
                          Verify against the source statement on the left.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Right Panel - Classification */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Classification</CardTitle>
                  <CardDescription className="text-zinc-500">
                    Assign category and expense type
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Category</Label>
                    <Select value={editData.category} onValueChange={(v) => setEditData({ ...editData, category: v })}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-zinc-800">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-800">{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Expense Type</Label>
                    <Select value={editData.expense_type || "other"} onValueChange={(v) => setEditData({ ...editData, expense_type: v })}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="expense-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-zinc-800">
                        {EXPENSE_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value} className="text-white hover:bg-zinc-800">{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                    <input
                      type="checkbox"
                      id="internal-transfer"
                      checked={editData.is_internal_transfer}
                      onChange={(e) => setEditData({ ...editData, is_internal_transfer: e.target.checked })}
                      className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-indigo-500 focus:ring-indigo-500"
                    />
                    <Label htmlFor="internal-transfer" className="text-zinc-300 cursor-pointer text-sm">
                      Internal transfer between own accounts
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Notes</Label>
                    <Textarea
                      placeholder="Add review notes..."
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600 min-h-[60px]"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={handleApprove}
                      disabled={saving}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="approve-btn"
                    >
                      {saving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Approve
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleSaveAndNext}
                      disabled={saving}
                      variant="outline"
                      className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      data-testid="save-next-btn"
                    >
                      Save & Next
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
